import React, {useEffect, useRef, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import axios from "axios";
import Api from "../../../../../core/services/api/api";
import {ReactComponent as CheckedBox} from "../../../../../assets/images/check-box/checkbox-active.svg";
import {ReactComponent as EmptyBox} from "../../../../../assets/images/check-box/checkbox.svg";
import LazyImage from "../../../app-specific/lazy-image";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import {Avatar, Chip, CircularProgress, useTheme} from "@mui/material";
import {OutlinedAutoComplete} from "../../../base/auto_complete/outlined_auto_complete";
import CacheService from "../../../../../core/services/cache/cache_service";

const OperatorsAutoComplete = ({selectedItems, setSelectedItems, currentOperatorId, multiple = true}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [autoCompleteText, setAutoCompleteText] = useState();
    const isMounted = useIsMounted();
    const theme = useTheme();
    const inputRef = useRef(null);

    /**
     * With each change in the selected items, updates the [selectedOptions] in the state
     */
    useEffect(() => {
        inputRef.current?.focus()
        setSelectedOptions(selectedItems?.map(e => ({
            title: e.firstName + " " + e.lastName,
            value: e,
        })) ?? []);
    }, [selectedItems])

    /**
     * Listens for the changes in autoCompleteText and with each change, if the length of the autoComplete text is
     * more than 3 characters, then calls the function for fetching list of operators
     */
    useEffect(() => {
        if (!isMounted())
            return;
        const axiosCancelToken = axios.CancelToken.source();
        let timer = getAutoCompleteItems(autoCompleteText, axiosCancelToken);
        return () => {
            if (timer) clearTimeout(timer);
            else axiosCancelToken.cancel();
        }
    }, [autoCompleteText, isMounted])

    /**
     * creates a time-out function for getting the auto complete suggestions
     * @param {string} autoCompleteText
     * @param {import('axios').CancelTokenSource} axiosCancelToken
     * @return {number}
     */
    const getAutoCompleteItems = (autoCompleteText, axiosCancelToken) => {
        return setTimeout(() => {
            if (!isMounted())
                return;
            getData(autoCompleteText, axiosCancelToken).then();
        }, 500);
    }

    /**
     * Fetches the list of operators from the server based on the keywords.
     * @param keywords {string}
     * @param {import('axios').CancelTokenSource} axiosCancelToken
     */
    const getData = async (keywords, axiosCancelToken) => {
        setIsLoading(true);
        const response = await Api.searchOperatorsLocal({paginationInfo: null, filters: {Name: !keywords?.length ? " " : keywords}}, axiosCancelToken.token);
        if (!isMounted())
            return;
        setIsLoading(false);
        if (response?.resultFlag) {
            setOptions(response?.data?.resultList ?? []);
        }
    }

    /**
     * Handles clearing the auto complete text field by the
     */
    const onTextCleared = () => {
        setSelectedItems([])
        setSelectedOptions([])
    }

    /**
     * Closes the autocomplete popper.
     *
     * - Ignores closing the popper when option is selected or is being removed
     * @param {Event} e
     * @param {string} reason
     */
    const onClose = (e, reason) => {
        switch (reason) {
            default:
                setOpen(false);
        }
    }

    /**
     * Sets the [selectedOptions] state value with the provided selectedOptions form the autocomplete.
     *
     * @param event
     * @param selectedOption
     */
    const onSelectionChanged = (event, selectedOption) => {
        setSelectedItems(Array.isArray(selectedOption) ? selectedOption.map(e => e.value) : [selectedOption.value]);
    }

    /**
     * Renders the list of options in the suggestions pop-over
     * @param option
     * @param isSelected
     * @return ReactNode
     */
    const renderOption = (option, isSelected) => {
        if (!option) return '';
        return (
            <div className={'operator-options'}>
                <div className={'operator-details'}>
                    <div className={'option-image'}>
                        <LazyImage
                            alt={'profile'}
                            noImage={<div style={{backgroundColor: `${theme.palette.background.default}`}}>
                                <ImagePlaceholder/>
                            </div>}
                            src={CacheService.getOperatorProfileImage(option.value?.id)}
                            height={35}
                        />
                    </div>
                    <div className={'operator-name'}>
                        <p className={'ml-2'}>{`${option?.value.firstName ?? ""} ${option?.value.lastName ?? ""}`}</p>
                    </div>
                </div>
                {
                    multiple &&
                    (
                        isSelected
                            ? <CheckedBox/>
                            : <EmptyBox/>
                    )
                }
            </div>
        );
    }

    return (
        <>
            <OutlinedAutoComplete
                onRemove={onTextCleared}
                inputRef={inputRef}
                focusOnRender={true}
                className={'operator-auto-complete'}
                selectOnFocus={true}
                fullWidth
                multiple={multiple}
                forcePopupIcon
                includeDefaultEndAdornment
                freeSolo={false}
                renderTags={(value, getTagProps) => (
                    value.map((option, index) => (
                        <Chip
                            key={index}
                            {...getTagProps({index})}
                            avatar={
                                <Avatar
                                    component={LazyImage}
                                    alt={'profile'}
                                    noImage={<ImagePlaceholder height={32}/>}
                                    src={CacheService.getOperatorProfileImage(option.value?.id)}
                                />
                            }
                            label={option.title}
                        />
                    ))
                )}
                loading={isLoading}
                label={'Select Operator'}
                placeholder={'Enter operator name'}
                type={'text'}
                variant={'outlined'}
                value={!multiple ? (selectedOptions[0] ?? '') : selectedOptions ?? ''}
                inputValue={autoCompleteText ?? ''}
                open={open}
                onOpen={() => setOpen(true)}
                getOptionSelected={(option, value) => (option.value.id === value?.value?.id)}
                onSelect={onSelectionChanged}
                onOptionUnselected={onSelectionChanged}
                onClose={onClose}
                onChange={(e) => setAutoCompleteText(e.target.value)}
                options={[...options, ...(selectedOptions?.map(e => e.value) ?? []), ...(selectedItems ?? [])]
                    .reduce((acc, current) => {
                        if (!current)
                            return acc;
                        const x = acc.find(item => item.id === current.id);
                        if (!x)
                            return [...acc, current]
                        return acc;
                    }, [])
                    .map(item => ({
                        title: `${item.firstName ?? ""} ${item.lastName ?? ""}`,
                        value: item
                    }))
                    .filter(e => e?.value?.id !== currentOperatorId)}
                renderOption={(props, option, {selected}) => renderOption(option, selected)}
                popupIcon={
                    isLoading
                        ? <CircularProgress size={25} color={'primary'}/>
                        : <></>
                }
                loadingText={'Loading...'}
            />
        </>
    );
}

export default OperatorsAutoComplete;
